/* eslint-disable camelcase */
/**
 * One variant of a product, identified by a SKU DERIVED from the código plus
 * the characteristics the host named — "301-PRETO-38". Derived rather than
 * supplied: extraction never produces a SKU, and the variant id it does
 * produce is an index within one extraction run.
 */
export interface AIProductSku {
  sku: string;
  color?: string;
  size?: string;
  /**
   * Stock of THIS variant — "uma unidade de cada tamanho". Undefined
   * means nobody said, and the product-wide quantity applies.
   */
  quantity?: number;
  /**
   * Where `quantity` came from: `spoken` (the host announced it in the
   * live), `seller` (she typed it) or `assumed` (nobody said, so a 1 is
   * there for her to adjust). An `assumed` number must never be shown as
   * confirmed stock, and the matcher never caps an allocation on one.
   */
  quantitySource?: 'spoken' | 'seller' | 'assumed';
}

export interface AIShoppingCartInfo {
  bedrockRegion: string;
  bedrockModelId: string;
  bedrockMaxTokens: number;
  secondsTaken: number;
  messageCount: number;
}

export interface AIProductDescriptors {
  color?: string[];
  size?: string[];
  price?: string[];
  other?: string[];
}

export interface AIProductData {
  id: string;
  label: string;
  codes: string[];
  descriptors?: AIProductDescriptors;
  price?: number;
  // Every price the host stated for this SKU, sorted min..max, on the
  // lives that quote more than one — usually by payment method
  // ("44,90 no cartão, 39,90 no pix"). `price` above is the LOWEST of
  // these, so a buyer is never shown a price for a payment method they
  // have not chosen. Undefined on the large majority of lives, which
  // quote a single price: absent means "one price", not "unknown".
  prices?: number[];
  // Those prices keyed by the payment method, wherever the host said it
  // out loud, e.g. { pix: 39.9, cartao: 44.9 }.
  pricesByMethod?: Record<string, number>;
  // Set when the announced value covers SEVERAL units — "os três por
  // dez reais" is 3 for R$10, not R$10 each. The unit price is never
  // derived, because the host never said it; the seller resolves it.
  bundleQty?: number;
  // Stock cap configured on the automation product. `undefined`
  // means "unlimited" (or the source never carried stock — e.g. AI-
  // extracted products); `0` means "sold out". Used by the
  // seller-facing live products tab so the seller sees catalogue
  // depth at a glance.
  quantity?: number;
  confidence: number;
  messagesIds: string[];
  // Optional thumbnail. Stamped by the automation path from
  // `automationProducts.imageUrl`; AI-extracted products today land
  // here without an image (the FE falls back to a placeholder tile).
  imageUrl?: string;
  // Long-form description carried over from the automation
  // catalogue. Shown read-only in the seller's live edit drawer
  // alongside the editable fields.
  description?: string;
  // Demand signal — total units claimed across every buyer for this
  // SKU. Pre-computed by the keyword-match upsert; unset on legacy
  // rows the new path never re-touched.
  /**
   * [variant-row] This product's VARIANT CATALOGUE — every SKU it has, not
   * only the ones a buyer already ordered, so the seller can set a quantity
   * on the 37 before anyone asks for it. Absent when the product has a
   * single SKU.
   */
  skus?: AIProductSku[];
  /** How many distinct variants of this product were claimed. */
  cartSkus?: number;
  cartQuantity?: number;
  // Distinct buyer count behind `cartQuantity`. Lets the seller's
  // card distinguish "1 buyer wants 3" from "3 buyers want 1".
  cartBuyers?: number;
  // Sum of units inside the stock cap (guaranteed demand). When
  // `quantity` is unlimited this equals `cartQuantity`.
  cartConfirmed?: number;
  // Sum of units beyond the stock cap (waitlist). 0 when stock fits
  // or is unlimited.
  cartWaitlisted?: number;
}

export function formatProductLabel(product: AIProductData): string {
  const codes = product.codes || [];
  const label = product.label || '';
  const descriptors = product.descriptors || {};

  const detailParts: string[] = [];
  if (descriptors.color?.length) detailParts.push(...descriptors.color);
  if (descriptors.size?.length) detailParts.push(...descriptors.size);
  if (descriptors.price?.length) detailParts.push(...descriptors.price);
  if (descriptors.other?.length) detailParts.push(...descriptors.other);

  const codeStr = codes.join(', ');
  const detailStr = detailParts.join(', ');

  const isRedundantLabel = codes.length > 0
    && /^(cod\.?|código)\s*/i.test(label.trim())
    && codes.some((c) => label.includes(c));

  const parts: string[] = [];
  if (codeStr) parts.push(codeStr);
  if (label && !isRedundantLabel) parts.push(label);
  if (detailStr) parts.push(detailStr);

  return parts.join(' | ') || label;
}

// Per-line annotation channel mirrored from the agents cart. Typed ONLY with
// the keys the FE actually renders — the backend may emit others (they stay on
// the runtime object, just undeclared here). Value is a tooltip ("" = bare).
// [cart-comments] per-line cart annotation channel (mirrors the
// backend). grep: cart-comments
export interface CartLineComments {
  /** vague / low-confidence purchase — key PRESENCE drives the indicator (value ""). */
  low_confidence?: string;
  /** buyer released the piece after ordering. */
  cancelado?: string;
  /** order placed for someone else. */
  terceiro?: string;
  /** buyer asked to swap pieces. */
  troca?: string;
  /** two typed codes in two minutes with the same descriptor. */
  correcao?: string;
  /** two codes, one name, one message — a catalogue duplicate. */
  duplicado?: string;
  /** price taken from the named variant's rule. */
  preco_variante?: string;
  /** the extraction captured no price for this product. */
  'sem-preco'?: string;
  /** the announced price covers several units. */
  preco_pacote?: string;
  /** the code has several variants and the buyer named none. */
  variante?: string;
  /** she named MORE THAN ONE variant of one code; the row was summed. */
  duas_variantes?: string;
  /** the LLM line audit contradicted this line. */
  revisar_ia?: string;
  /** generic review reason (mirror alias). */
  revisar?: string;
  /** item carried over from an earlier cart run — confirm before charging. */
  consenso?: string;
  /** the size came from another message of hers, not from the order. */
  tamanho_inferido?: string;
  /** she typed the bare code N times; the row was summed to N units. */
  codigo_repetido?: string;
  /**
   * LEGACY spelling of `cancelado`. No current writer produces it, but 16
   * production rows still carry it, and a buyer who released her piece must
   * not go unnoticed just because the key was renamed.
   */
  cancelled?: string;
  // ── descriptive, NOT review reasons (see DESCRIPTIVE_COMMENT_KEYS) ──
  /** the size the buyer stated, for the seller to ship by. */
  tamanho?: string;
  /** a "size" that was really the quantity restated. */
  tamanho_ignorado?: string;
  /** the LLM line audit checked this line and believed it. */
  verificado?: string;
}

/**
 * True when `key` is PRESENT in `comments` (the value may be ""). Mirrors the
 * backend `has_comment` — presence, not truthiness, so a bare "" flag counts.
 */
export const hasComment = (
  comments: CartLineComments | undefined,
  key: keyof CartLineComments,
): boolean => comments != null && key in comments;

/**
 * Comments that DESCRIBE a line rather than explain a review. Mirrors
 * `DESCRIPTIVE_COMMENT_KEYS` in the backend's review_flags.py, and the two
 * lists must stay in sync: a descriptive key counted as a reason gives the
 * seller a review dot whose tooltip reads "37".
 */
export const DESCRIPTIVE_COMMENT_KEYS: ReadonlySet<string> = new Set([
  'tamanho',
  'tamanho_ignorado',
  'verificado',
  // `codigo_repetido` was here until 2026-10-21, matching the backend: the
  // code WAS typed twice and both units are billed on purpose, so saying so
  // was not a doubt. The 13-live QA reversed that — repetition multiplied
  // the quantity on a UNIQUE piece and nothing on screen caught it. It is a
  // REASON on both sides now, and the two sides must ship together: a key
  // that is a reason here and descriptive there lights a dot with the
  // generic "confirmar antes de cobrar" on top of the useful sentence.
]);

/**
 * Review reasons in the order the seller should read them: the ones that
 * change whether to charge at all come before the ones that change how much.
 */
export const REVIEW_REASON_KEYS: readonly (keyof CartLineComments)[] = [
  'cancelado',
  'cancelled',
  'revisar_ia',
  'consenso',
  'tamanho_inferido',
  'terceiro',
  'troca',
  'variante',
  'duas_variantes',
  'duplicado',
  'correcao',
  'codigo_repetido',
  'preco_pacote',
  'preco_variante',
  'sem-preco',
  'low_confidence',
  'revisar',
];

export interface CartLineReviewReason {
  key: string;
  /** The backend's own explanation. Empty when the key is a bare flag. */
  detail: string;
}

/**
 * Every review reason present on a line, most consequential first.
 *
 * The panel used to light a yellow dot on `low_confidence` alone and show one
 * generic tooltip, so fifteen distinct backend reasons — cancelled, ordered
 * for someone else, variant not named, no price captured — all reached the
 * seller as the same unexplained dot. Descriptive keys are excluded: a size
 * is not a reason to review.
 */
export function cartLineReviewReasons(
  comments: CartLineComments | undefined,
): CartLineReviewReason[] {
  if (comments == null) return [];
  const out: CartLineReviewReason[] = [];
  REVIEW_REASON_KEYS.forEach((key) => {
    if (!(key in comments)) return;
    if (DESCRIPTIVE_COMMENT_KEYS.has(key as string)) return;
    out.push({ key: key as string, detail: String(comments[key] ?? '') });
  });
  // A key the backend added but the FE has not declared yet still deserves
  // to reach the seller, rather than being silently swallowed.
  Object.keys(comments).forEach((key) => {
    if (DESCRIPTIVE_COMMENT_KEYS.has(key)) return;
    if (REVIEW_REASON_KEYS.includes(key as keyof CartLineComments)) return;
    out.push({ key, detail: String((comments as Record<string, unknown>)[key] ?? '') });
  });
  return out;
}

/** True when the line carries any reason the seller should look at. */
export function needsReview(comments: CartLineComments | undefined): boolean {
  return cartLineReviewReasons(comments).length > 0;
}

// ── Cart-line evidence (low-confidence inspect modal) ─────────────────
// [cart-comments] grep: cart-comments
//
// Response of POST /plugins/live/carts/evidence (Go → agent-controller).
// The wire comes snake_cased from Python; the parser normalizes to
// camelCase and guarantees the arrays exist — the modal never deals
// with undefined.

export interface CartLineEvidenceMessage {
  id: string;
  text: string;
  timestamp: string; // ISO
}

export interface CartLineEvidencePhrase {
  start: number;
  end: number | null; // some phrases carry no end time
  text: string;
  // Wall-clock HH:MM:SS of the phrase (same clock as the messages) so
  // the seller can line comment and speech up. "" when unavailable.
  time: string;
}

export interface CartLineEvidence {
  buyerMessages: CartLineEvidenceMessage[];
  transcriptAroundPurchase: CartLineEvidencePhrase[];
}

export function parseCartLineEvidence(raw: any): CartLineEvidence {
  return {
    buyerMessages: (raw?.buyer_messages ?? []).map((m: any) => ({
      id: m.id ?? '',
      text: m.text ?? '',
      timestamp: m.timestamp ?? '',
    })),
    transcriptAroundPurchase: (raw?.transcript_around_purchase ?? []).map((p: any) => ({
      start: typeof p.start === 'number' ? p.start : 0,
      end: typeof p.end === 'number' ? p.end : null,
      text: p.text ?? '',
      time: p.time ?? '',
    })),
  };
}

export interface AIProductCartUser {
  // Canonical buyer id (Instagram numeric id for chat-driven
  // intents). Drives admin cart mutations so a seller +1 lands in
  // the same intent bucket — without it the seller's user_id
  // would default to `username` and Python would create a sibling
  // bucket, duplicating the cart line.
  userId?: string;
  username: string;
  qty: number;
  messagesIds: string[];
  confidence: number;
  // 1-indexed position in the per-product queue, by earliest claim
  // timestamp. Unset on legacy rows.
  joinedOrder?: number;
  // First stock slot this buyer occupies (1-indexed).
  slotStart?: number;
  // qty split against stock: units within cap + units past cap.
  unitsConfirmed?: number;
  unitsWaitlisted?: number;
  // Per-line annotations (see CartLineComments).
  comments?: CartLineComments;
  // The size the buyer stated ("37", "GG", "aro 18"), or several joined by
  // "/" when one code was claimed in more than one size. The agents mirror
  // has written this for a while; Go only began declaring it alongside this
  // field, so legacy responses simply omit it.
  size?: string;
  // Which VARIANT of the product this line bills. Until 2026-10-12 a line
  // carried only the código plus a label whose text might hold the colour,
  // so two siblings could only be told apart by reading it — 5 of 19
  // confirmed cart errors were a colour or size the buyer DID name being
  // replaced by a sibling. Absent when the product has no variant axis or
  // her words did not narrow it to one.
  /**
   * The VARIANT this line is for, derived from the código plus the colour
   * and size the host and the buyer named — "301-38", "473-AZULMARINHO-38".
   * The mirror writes one line per variant, so this is what distinguishes
   * two lines of the same product for the same buyer.
   */
  sku?: string;
  variantId?: string;
  // The seller's own SKU for that variant, when her catalogue carries one.
  // This is the join to her inventory; empty until the catalogue is wired in.
  merchantSku?: string;
  // The colour of the billed variant, verbatim from the catalogue.
  variantColor?: string;
  // The seller changed this line by hand and a later re-run KEPT her
  // version instead of overwriting it. Show it as hers, not as the AI's:
  // before this existed, re-processing a live silently replaced every
  // correction she had made.
  sellerEdited?: boolean;
}

export interface AIProducts {
  createdAt: Date;
  updatedAt: Date;
  status: string;
  info?: AIShoppingCartInfo;
  error?: string;
  // "collection" → seller-curated catalogue (no need to upload
  // video to refine). "ai" / undefined → transcript-extracted.
  source?: string;
  data: AIProductData[];
}

export interface AIProductCart {
  productId: string;
  users: AIProductCartUser[];
}

export interface AIProductsCarts {
  createdAt: Date;
  updatedAt?: Date;
  status: string;
  info?: AIShoppingCartInfo;
  error?: string;
  percentage?: number;
  data: AIProductCart[];
}

export interface Comment {
  text: string;
  timestamp: Date;
}

export function formatTimestamp(timestamp: Date | string): string {
  if (typeof timestamp === 'string' && timestamp === '') {
    return 'N/A';
  }

  const time = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;

  const userLocale = localStorage.getItem('user-locale') || 'pt-BR';
  return time.toLocaleTimeString(userLocale, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * A message that read as a purchase but bound to no product — so nothing was
 * billed. These are the possible LOST SALES: the pipeline deliberately keeps
 * them rather than discarding them, and the seller is the one who can tell
 * whether any of them was a real order.
 */
export interface AIUnmatchedIntent {
  userName: string;
  /** Whatever the buyer named; often the whole message. */
  productHint?: string;
  message: string;
  intentClass?: string;
  resolutionMethod?: string;
  confidence?: number;
}

/**
 * A live-level finding about the cart run: a prize draw whose guesses were
 * not billed, a catalogue that did not cover the codes buyers typed.
 */
export interface AILiveCondition {
  code: string;
  severity?: string;
  message: string;
  /** What the seller can do about it. */
  action?: string;
}

export interface AICartDiagnostics {
  updatedAt?: Date;
  unmatchedIntents: AIUnmatchedIntent[];
  liveConditions: AILiveCondition[];
}

/** Normalizes the snake_cased wire shape; guarantees both arrays exist. */
export function parseCartDiagnostics(raw: any): AICartDiagnostics | undefined {
  if (raw == null) return undefined;
  const at = raw.updated_at ?? raw.updatedAt;
  return {
    updatedAt: typeof at === 'string' ? new Date(at) : undefined,
    unmatchedIntents: (raw.unmatched_intents ?? raw.unmatchedIntents ?? []).map((u: any) => ({
      userName: u.user_name ?? u.userName ?? '',
      productHint: u.product_hint ?? u.productHint ?? '',
      message: u.message ?? '',
      intentClass: u.intent_class ?? u.intentClass ?? '',
      resolutionMethod: u.resolution_method ?? u.resolutionMethod ?? '',
      confidence: typeof (u.confidence) === 'number' ? u.confidence : undefined,
    })),
    liveConditions: (raw.live_conditions ?? raw.liveConditions ?? []).map((c: any) => ({
      code: c.code ?? '',
      severity: c.severity ?? '',
      message: c.message ?? '',
      action: c.action ?? '',
    })),
  };
}

export interface InstagramLiveAIMetadata {
  _id: string;
  mediaId: string;
  accountId: string;
  timestamp: Date;
  status: string;
  createdAt: Date;
  updatedAt: Date;
  error?: string;
  products?: AIProducts;
  productsCart?: AIProductsCarts;
  // What the cart run noticed but could not bill. Undefined on rows written
  // before it existed, and on rows whose last writer was the automation
  // path — undefined means "nobody told us", not "there were none".
  cartDiagnostics?: AICartDiagnostics;
  commentsById?: Record<string, Comment>;
}

export class InstagramLiveAIMetadataModel implements InstagramLiveAIMetadata {
  _id: string;

  mediaId: string;

  accountId: string;

  timestamp: Date;

  status: string;

  createdAt: Date;

  updatedAt: Date;

  error?: string;

  products?: AIProducts;

  productsCart?: AIProductsCarts;

  cartDiagnostics?: AICartDiagnostics;

  commentsById?: Record<string, Comment>;

  constructor(data: InstagramLiveAIMetadata) {
    // eslint-disable-next-line no-underscore-dangle
    this._id = data._id;
    this.mediaId = data.mediaId;
    this.accountId = data.accountId;
    this.timestamp = data.timestamp;
    this.status = data.status;
    this.createdAt = data.createdAt;
    this.updatedAt = data.updatedAt;
    this.error = data.error;
    this.products = data.products;
    this.productsCart = data.productsCart;
    this.cartDiagnostics = data.cartDiagnostics;
    this.commentsById = data.commentsById;
  }

  static fromApiResponse(columns: string[], rows: any[][]): InstagramLiveAIMetadataModel[] {
    if (rows.length === 0) return [];

    return rows.map((row: any[]) => {
      const result: any = {};

      columns.forEach((column, index) => {
        const camelCaseKey = column.replace(/_([a-z])/g, (_, char) => char.toUpperCase());
        const value = row[index];

        // Date fields
        if (
          ['createdAt', 'updatedAt', 'timestamp'].includes(camelCaseKey)
        && typeof value === 'string'
        ) {
          result[camelCaseKey] = new Date(value);
          return;
        }

        // products
        if (camelCaseKey === 'products' && value) {
          result.products = {
            createdAt: new Date(value.created_at),
            updatedAt: new Date(value.updated_at),
            status: value.status,
            source: value.source,
            info: value.info && {
              bedrockRegion: value.info.bedrock_region,
              bedrockModelId: value.info.bedrock_model_id,
              bedrockMaxTokens: value.info.bedrock_max_tokens,
              secondsTaken: value.info.seconds_taken,
              messageCount: value.info.message_count,
            },
            error: value.error,
            data: value.data?.map((item: any) => ({
              id: item.id,
              label: item.label,
              codes: item.codes,
              descriptors: item.descriptors,
              price: item.price,
              quantity: item.quantity,
              confidence: item.confidence,
              messagesIds: item.messages_ids,
              imageUrl: item.image_url || item.imageUrl,
              description: item.description,
              // Field by field, like the product above: a pass-through would
              // leave `quantity_source` in snake_case and the panel could
              // not tell the host's announced stock from the assumed 1.
              skus: item.skus?.map((sku: any) => ({
                sku: sku.sku,
                color: sku.color,
                size: sku.size,
                quantity: sku.quantity,
                quantitySource: sku.quantity_source ?? sku.quantitySource,
              })),
              cartSkus: item.cart_skus ?? item.cartSkus,
              cartQuantity: item.cart_quantity ?? item.cartQuantity,
              cartBuyers: item.cart_buyers ?? item.cartBuyers,
              cartConfirmed: item.cart_confirmed ?? item.cartConfirmed,
              cartWaitlisted: item.cart_waitlisted ?? item.cartWaitlisted,
              // Dual pricing. This mapper builds each product field by
              // field, so anything not named here is dropped before the
              // panel ever sees it — the reason the first version of these
              // fields rendered nowhere. Both spellings are accepted: the Go
              // API emits snake_case, the Mongo mirror camelCase.
              prices: item.prices,
              pricesByMethod: item.prices_by_method ?? item.pricesByMethod,
              bundleQty: item.bundle_qty ?? item.bundleQty,
            })),
          };
          return;
        }

        // products_cart
        if (camelCaseKey === 'productsCart' && value) {
          result.productsCart = {
            createdAt: new Date(value.created_at),
            updatedAt: value.updated_at ? new Date(value.updated_at) : undefined,
            status: value.status,
            info: value.info && {
              bedrockRegion: value.info.bedrock_region,
              bedrockModelId: value.info.bedrock_model_id,
              bedrockMaxTokens: value.info.bedrock_max_tokens,
              secondsTaken: value.info.seconds_taken,
              messageCount: value.info.message_count,
            },
            error: value.error,
            percentage: value.percentage,
            data: (value.data ?? []).map((item: any) => ({
              productId: item.product_id,
              users: item.users.map((user: any) => ({
                userId: user.user_id ?? user.userId,
                username: user.username,
                qty: user.qty,
                confidence: user.confidence,
                messagesIds: user.messages_ids,
                joinedOrder: user.joined_order ?? user.joinedOrder,
                slotStart: user.slot_start ?? user.slotStart,
                unitsConfirmed: user.units_confirmed ?? user.unitsConfirmed,
                unitsWaitlisted: user.units_waitlisted ?? user.unitsWaitlisted,
                comments: user.comments,
                size: user.size,
                // Declared in the type is not enough: a field the normalizer
                // leaves out never reaches the component, so the panel would
                // show the type and never the data.
                sku: user.sku,
                variantId: user.variant_id ?? user.variantId,
                merchantSku: user.merchant_sku ?? user.merchantSku,
                variantColor: user.variant_color ?? user.variantColor,
                sellerEdited: user.seller_edited ?? user.sellerEdited,
              })) as AIProductCartUser[],
            })) as AIProductCart[],
          };
          return; // ✅ Prevents raw 'value' from being set below
        }

        // cart_diagnostics — unmatched purchases + live-level findings
        if (camelCaseKey === 'cartDiagnostics') {
          result.cartDiagnostics = parseCartDiagnostics(value);
          return;
        }

        // commentsById
        if (camelCaseKey === 'commentsById' && value) {
          result.commentsById = Object.fromEntries(
            Object.entries(value).map(([msgId, comment]: any) => [
              msgId,
              { text: comment.text, timestamp: new Date(comment.timestamp) },
            ]),
          );
          return; // ✅ Same here
        }

        // Default
        result[camelCaseKey] = value;
      });

      return new InstagramLiveAIMetadataModel(result);
    });
  }
}
